<template>
  <div class="categorias-contenedor">
    <h2 class="mb-4">Categorías de Productos</h2>

    <div class="mb-3">
      <button v-if="puedeCrear" @click="mostrarModal(null)" class="btn-primario">
        <i class="bi bi-plus-circle me-1"></i> Nueva Categoría
      </button>
    </div>

    <div class="tarjeta">
      <div class="tarjeta-cuerpo">
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border" role="status" style="color: var(--primary-main);">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>
        <div v-else-if="categorias.length === 0" class="alerta-info">
          No se encontraron categorias registradas.
        </div>
        <div v-else class="table-responsive">
          <table class="tabla-datos">
            <thead>
              <tr>
                <th>ID</th>
                <th>Categoría</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="categoria in categorias" :key="categoria.id">
                <td>{{ categoria.id }}</td>
                <td><strong>{{ categoria.categoria }}</strong></td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button v-if="puedeEditar" @click="mostrarModal(categoria)" class="btn-accion btn-accion-editar" title="Editar">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button v-if="puedeEliminar" @click="confirmarEliminar(categoria)" class="btn-accion btn-accion-eliminar" title="Eliminar">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Crear/Editar -->
    <div v-if="modalVisible" class="modal-fondo">
      <div class="modal-contenido">
        <div class="modal-encabezado">
          <h5 class="modal-titulo">{{ categoriaEnEdicion ? 'Editar' : 'Nueva' }} Categoría</h5>
          <button type="button" class="btn-close" @click="cerrarModal"></button>
        </div>
        <div class="modal-cuerpo">
          <form @submit.prevent="guardarCategoria">
            <div class="campo-grupo">
              <label class="campo-etiqueta">Nombre *</label>
              <input v-model="formulario.categoria" type="text" class="campo-entrada" required placeholder="Nombre de la categoria" />
            </div>
          </form>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn-secundario" @click="cerrarModal">Cancelar</button>
          <button type="button" class="btn-primario" @click="guardarCategoria" :disabled="guardando">
            <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
            {{ guardando ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmar Eliminar -->
    <div v-if="categoriaParaEliminar" class="modal-fondo">
      <div class="modal-contenido">
        <div class="modal-encabezado">
          <h5 class="modal-titulo">Confirmar Eliminación</h5>
          <button type="button" class="btn-close" @click="categoriaParaEliminar = null"></button>
        </div>
        <div class="modal-cuerpo">
          <p>Desea eliminar la categoria <strong>{{ categoriaParaEliminar.categoria }}</strong>?</p>
          <p class="texto-secundario small">Esta acción no se puede deshacer.</p>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn-secundario" @click="categoriaParaEliminar = null">Cancelar</button>
          <button type="button" class="btn-peligro" @click="eliminarCategoria">Eliminar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inventarioService } from '@/services/inventarioService';
import notificationService from '@/services/notificationService';

export default {
  name: 'ClientInventarioCategorias',
  computed: {
    puedeCrear() {
      return this.$store.getters.puedoHacer('/inventario/categorias', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/inventario/categorias', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/inventario/categorias', 'eliminar')
    }
  },
  data() {
    return {
      categorias: [],
      cargando: false,
      modalVisible: false,
      guardando: false,
      categoriaEnEdicion: null,
      categoriaParaEliminar: null,
      formulario: { categoria: '' }
    };
  },
  mounted() {
    this.cargarCategorias();
  },
  methods: {
    async cargarCategorias() {
      this.cargando = true;
      try {
        const respuesta = await inventarioService.getCategorias();
        this.categorias = respuesta?.data || [];
      } catch (error) {
        console.error('Error al cargar categorias:', error);
        this.categorias = [];
      } finally {
        this.cargando = false;
      }
    },
    mostrarModal(categoria) {
      if (categoria) {
        this.categoriaEnEdicion = categoria;
        this.formulario = { categoria: categoria.categoria };
      } else {
        this.categoriaEnEdicion = null;
        this.formulario = { categoria: '' };
      }
      this.modalVisible = true;
    },
    cerrarModal() {
      this.modalVisible = false;
      this.categoriaEnEdicion = null;
      this.formulario = { categoria: '' };
    },
    async guardarCategoria() {
      if (!this.formulario.categoria.trim()) {
        notificationService.warning('El nombre de la categoria es requerido');
        return;
      }
      this.guardando = true;
      try {
        if (this.categoriaEnEdicion) {
          await inventarioService.updateCategoria(this.categoriaEnEdicion.id, this.formulario);
          notificationService.success('Categoría actualizada correctamente');
        } else {
          await inventarioService.createCategoria(this.formulario);
          notificationService.success('Categoría creada correctamente');
        }
        this.cerrarModal();
        this.cargarCategorias();
      } catch (error) {
        console.error('Error al guardar categoria:', error);
        notificationService.error('Error al guardar la categoria');
      } finally {
        this.guardando = false;
      }
    },
    confirmarEliminar(categoria) {
      this.categoriaParaEliminar = categoria;
    },
    async eliminarCategoria() {
      try {
        await inventarioService.deleteCategoria(this.categoriaParaEliminar.id);
        notificationService.success('Categoría eliminada correctamente');
        this.categoriaParaEliminar = null;
        this.cargarCategorias();
      } catch (error) {
        console.error('Error al eliminar categoria:', error);
        notificationService.error('Error al eliminar la categoria');
      }
    }
  }
};
</script>

<style scoped>
.categorias-contenedor { padding: 20px; color: var(--text-primary); }
.tarjeta { background-color: var(--secondary-bg-main); border: 1px solid var(--border-main); border-radius: 8px; box-shadow: var(--shadow-sm); }
.tarjeta-cuerpo { padding: 1rem; }
.tabla-datos { width: 100%; border-collapse: collapse; }
.tabla-datos th { background-color: var(--bg-main); color: var(--text-secondary); font-weight: 600; padding: 0.75rem; border-bottom: 1px solid var(--border-main); text-align: left; }
.tabla-datos td { padding: 0.75rem; border-bottom: 1px solid var(--border-main); color: var(--text-primary); }
.tabla-datos tr:hover td { background-color: var(--bg-main); }
.alerta-info { padding: 1rem; background-color: var(--bg-main); border: 1px solid var(--border-main); border-radius: 6px; color: var(--text-secondary); }
.texto-secundario { color: var(--text-secondary); }
.btn-primario { background-color: var(--primary-main); color: white; border: none; padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; }
.btn-primario:hover { background-color: var(--primary-hover); }
.btn-primario:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secundario { background-color: var(--bg-main); color: var(--text-primary); border: 1px solid var(--border-main); padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; }
.btn-peligro { background-color: var(--accent-danger); color: white; border: none; padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; }
.btn-accion { border: none; padding: 0.25rem 0.5rem; border-radius: 4px; cursor: pointer; margin: 0 2px; }
.btn-accion-editar { background-color: var(--primary-main); color: white; }
.btn-accion-eliminar { background-color: var(--accent-danger); color: white; }
.btn-accion:hover { opacity: 0.85; }
.modal-fondo { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.modal-contenido { background-color: var(--secondary-bg-main); border: 1px solid var(--border-main); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: var(--shadow-md); }
.modal-encabezado { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border-main); }
.modal-titulo { margin: 0; color: var(--text-primary); font-size: 1.1rem; }
.modal-cuerpo { padding: 1.25rem; }
.modal-pie { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 1rem 1.25rem; border-top: 1px solid var(--border-main); }
.campo-grupo { margin-bottom: 1rem; }
.campo-etiqueta { display: block; margin-bottom: 0.4rem; color: var(--text-secondary); font-size: 0.9rem; }
.campo-entrada { width: 100%; padding: 0.5rem 0.75rem; background-color: var(--bg-main); border: 1px solid var(--border-main); border-radius: 6px; color: var(--text-primary); font-size: 0.95rem; box-sizing: border-box; }
.campo-entrada:focus { outline: none; border-color: var(--border-focus); box-shadow: 0 0 0 2px rgba(14,165,233,0.15); }
</style>